import React from 'react';
import { useRefreshAccountStatusDefinitions, useUpdateAccountStatusDefinition } from '@app/sdk';
import { ComponentErrorBoundary } from '@app/utilities';
import { FormProvider, useForm } from 'react-hook-form';
import { FormContainer, Input, InputProps } from '@app/shared';

type AccountStatusDefinitionFormModel = {
  id: string;
  name: string;
  behaviourSettings: BehaviourSettingsFormModel;
};

type BehaviourSettingsFormModel = {
  acceptAuths: string;
  acceptClearing: string;
  accrueInterest: string;
  chargeInterest: string;
  chargeFees: string;
  generateStatements: string;
  allowRegularPaymentCollection: string;
  pauseBilling: string;
  allowStatementDayChange: string;
  enableNewDuesAndAging: string;
  enablePaymentDueDateNosiaCheck: string;
  enableRetrospectiveNosiaCheck: string;
  enableAutomaticChargeOff: string;
  generateAnnualStatement: string;
  enablePersistentDebtCalculation: string;
};

export function AccountStatusDefinitionForm({
  accountStatusDefinition,
  onUpdate,
}: {
  accountStatusDefinition: AccountStatusDefinitionFormModel;
  onUpdate: () => Promise<void>;
}) {
  const { mutateAsync: update, isPending: isUpdating } = useUpdateAccountStatusDefinition();
  const refreshAccountStatusDefinitions = useRefreshAccountStatusDefinitions();

  async function onSaveStatus(data: AccountStatusDefinitionFormModel) {
    if (accountStatusDefinition?.id) {
      const request = {
        name: data.name,
      };
      await update({ id: accountStatusDefinition.id, request });
    }
    await refreshAccountStatusDefinitions();
    await onUpdate();
  }

  const formContext = useForm<AccountStatusDefinitionFormModel>();

  const name: InputProps<AccountStatusDefinitionFormModel> = {
    name: 'name',
    label: 'Name',
    options: { required: 'Name is required' },
    defaultValue: accountStatusDefinition?.name,
  };

  return (
    accountStatusDefinition && (
      <ComponentErrorBoundary>
        <FormProvider {...formContext}>
          <FormContainer isSubmitting={isUpdating} onSubmit={onSaveStatus} disableButtons>
            <Input {...name} disabled />
            <div className={'ui card sixteen wide column'} style={{ marginTop: '0' }}>
              <div className="custom-card-header content" style={{ margin: '0', padding: '0', paddingBottom: '10px' }}>
                Behaviour Settings
              </div>
              <div className="content" style={{ margin: '0', paddingLeft: '0', paddingBottom: '0' }}>
                <div>
                  <span style={{ fontWeight: 'bold' }}>AcceptAuths</span>:{' '}
                  {accountStatusDefinition?.behaviourSettings.acceptAuths}
                </div>
                <div>
                  <span style={{ fontWeight: 'bold' }}>AcceptClearing</span>:{' '}
                  {accountStatusDefinition?.behaviourSettings.acceptClearing}
                </div>
                <div>
                  <span style={{ fontWeight: 'bold' }}>AccrueInterest</span>:{' '}
                  {accountStatusDefinition?.behaviourSettings.accrueInterest}
                </div>
                <div>
                  <span style={{ fontWeight: 'bold' }}>ChargeInterest</span>:{' '}
                  {accountStatusDefinition?.behaviourSettings.chargeInterest}
                </div>
                <div>
                  <span style={{ fontWeight: 'bold' }}>ChargeFees</span>:{' '}
                  {accountStatusDefinition?.behaviourSettings.chargeFees}
                </div>
                <div>
                  <span style={{ fontWeight: 'bold' }}>GenerateStatements</span>:{' '}
                  {accountStatusDefinition?.behaviourSettings.generateStatements}
                </div>
                <div>
                  <span style={{ fontWeight: 'bold' }}>AllowRegularPaymentCollection</span>:{' '}
                  {accountStatusDefinition?.behaviourSettings.allowRegularPaymentCollection}
                </div>
                <div>
                  <span style={{ fontWeight: 'bold' }}>PauseBilling</span>:{' '}
                  {accountStatusDefinition?.behaviourSettings.pauseBilling}
                </div>
                <div>
                  <span style={{ fontWeight: 'bold' }}>AllowStatementDayChange</span>:{' '}
                  {accountStatusDefinition?.behaviourSettings.allowStatementDayChange}
                </div>
                <div>
                  <span style={{ fontWeight: 'bold' }}>EnableNewDuesAndAging</span>:{' '}
                  {accountStatusDefinition?.behaviourSettings.enableNewDuesAndAging}
                </div>
                <div>
                  <span style={{ fontWeight: 'bold' }}>EnablePaymentDueDateNosiaCheck</span>:{' '}
                  {accountStatusDefinition?.behaviourSettings.enablePaymentDueDateNosiaCheck}
                </div>
                <div>
                  <span style={{ fontWeight: 'bold' }}>EnableRetrospectiveNosiaCheck</span>:{' '}
                  {accountStatusDefinition?.behaviourSettings.enableRetrospectiveNosiaCheck}
                </div>
                <div>
                  <span style={{ fontWeight: 'bold' }}>EnableAutomaticChargeOff</span>:{' '}
                  {accountStatusDefinition?.behaviourSettings.enableAutomaticChargeOff}
                </div>
                <div>
                  <span style={{ fontWeight: 'bold' }}>GenerateAnnualStatement</span>:{' '}
                  {accountStatusDefinition?.behaviourSettings.generateAnnualStatement}
                </div>
                <div>
                  <span style={{ fontWeight: 'bold' }}>EnablePersistentDebtCalculation</span>:{' '}
                  {accountStatusDefinition?.behaviourSettings.enablePersistentDebtCalculation}
                </div>
              </div>
            </div>
          </FormContainer>
        </FormProvider>
      </ComponentErrorBoundary>
    )
  );
}
